import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getManagementScratchOrders, getManagementOrderById } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError } from '../utils/helpers';
import ManagementOrderDetailsView from './ManagementOrderDetailsView';
import SuccessModal from '../components/common/SuccessModal';
import WholesalerName from '../components/common/WholesalerName';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ManagementScratchOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // Store original page data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [totalFromAPI, setTotalFromAPI] = useState(0); // Store total from API
    const [itemsPerPage] = useState(20);
    const [searchLoading, setSearchLoading] = useState(false);

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    // Refs for debouncing and request cancellation
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const navigate = useNavigate();
    const { accessToken } = useAuth();

    // Calculate pagination for filtered results
    const calculatePagination = (filteredData) => {
        const total = filteredData.length;
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        setTotalElements(total);

        // Reset to page 1 if current page is beyond new total pages
        if (page > pages && pages > 0) {
            setPage(1);
        }
    };

    // Get current page data - for server-side pagination, this is just the current orders
    const getCurrentPageData = () => {
        // Since we're using server-side pagination, the current orders are already the page data
        return filteredOrders;
    };

    // Fetch all orders (paginated)
    const fetchOrders = async () => {
        if (!accessToken) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const data = await getManagementScratchOrders({ page: page, size: 20 });
            console.log('Sample order data:', data?.content?.[0] || data?.[0]); // Debug first order

            // Handle different response structures
            let pageOrders = [];
            if (Array.isArray(data)) {
                // If data is directly an array
                pageOrders = data;
            } else if (data && data.content && Array.isArray(data.content)) {
                // If data has a content property that's an array
                pageOrders = data.content;
            } else if (data && Array.isArray(data.orders)) {
                // If data has an orders property
                pageOrders = data.orders;
            } else if (data && typeof data === 'object') {
                // If data is an object but doesn't have expected properties, check if it's a single order
                pageOrders = [data];
            } else {
                pageOrders = [];
            }

            // Sort orders by ID in descending order (biggest number at top)
            const sortedOrders = pageOrders.sort((a, b) => {
                const idA = parseInt(a.id) || 0;
                const idB = parseInt(b.id) || 0;
                return idB - idA; // Descending order
            });

            // Set state
            setOrders(sortedOrders);
            setOriginalOrders(sortedOrders); // Store original page data
            setFilteredOrders(sortedOrders);

            // Handle pagination data
            const totalPages = data?.totalPages || data?.total_pages || Math.ceil((data?.totalElements || data?.total_elements || data?.total || pageOrders.length) / itemsPerPage) || 1;
            const totalElements = data?.totalElements || data?.total_elements || data?.total || pageOrders.length;

            setTotalPages(Number(totalPages));
            setTotalElements(Number(totalElements));
            setTotalFromAPI(Number(totalElements));
        } catch (err) {
            console.error('Error fetching scratch orders:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                status: err.status
            });

            // Clear any existing orders on error
            setOrders([]);
            setFilteredOrders([]);
            setOriginalOrders([]);

            if (handleApiError(err, navigate)) return;
            setError('Failed to fetch scratch orders: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchOrders();
        }
        // eslint-disable-next-line
    }, [accessToken, page]);



    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        // Check if search query is empty or just whitespace
        if (!query || !query.trim()) {
            setFilteredOrders(originalOrders);
            setTotalElements(originalOrders.length);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        try {
            const order = await getManagementOrderById(query, 'ALL');
            if (abortControllerRef.current?.signal.aborted) {
                return;
            }
            if (order && order.id) {
                setFilteredOrders([order]);
                setTotalElements(1);
            } else {
                setFilteredOrders([]);
                setTotalElements(0);
            }
        } catch (err) {
            if (abortControllerRef.current?.signal.aborted) {
                return;
            }
            setFilteredOrders([]);
            setTotalElements(0);
        }
        setSearchLoading(false);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getManagementOrderById(orderId, 'ALL');
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleOrderUpdate = (orderId, newStatus, successData = null) => {
        // Check if this is a refresh request
        if (successData && successData.refresh && successData.order) {
            // Update the selected order with refreshed data
            setSelectedOrder(successData.order);
            return;
        }

        // Handle normal order updates (status changes, etc.)
        setFilteredOrders(prev => prev.filter(order => order.id !== orderId));
        setTotalElements(prev => prev - 1);

        setSelectedOrder(null);
        setDetailsError(null);

        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }
    };

    const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };


    if (error) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                        {error}
                    </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Scratch Orders Management</h1>
                <p className="text-gray-600">View and manage scratch card orders that are not yet delivered.</p>
            </div>

            {!selectedOrder && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search Orders</label>
                            <input
                                type="text"
                                placeholder="Search by Order ID..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {searchLoading && (
                                <div className="mt-2 text-sm text-gray-500">Searching...</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {selectedOrder ? (
                <div>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                        )
                    ) : (
                        <ManagementOrderDetailsView order={selectedOrder} onOrderUpdate={handleOrderUpdate} pageType="scratch" />
                    )}
                </div>
            ) : (
                <>
                    {/* <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search Orders</label>
                                <input
                                    type="text"
                                    placeholder="Search by Order ID..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {searchLoading && (
                                    <div className="mt-2 text-sm text-gray-500">Searching...</div>
                                )}
                            </div>
                        </div>
                    </div> */}
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {getCurrentPageData().length} of {totalFromAPI} orders
                    </div>

                    {(loading || tableLoading) ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                            <p className="text-gray-500 text-lg">No orders found</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredOrders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>{order.assignedDealer || order.dealerName || '-'}</div>
                                                    {order.wholesalerId && (
                                                        <WholesalerName wholesalerId={order.wholesalerId} />
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(order.createdAt)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order.id)}>View Details</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 mb-2 w-full">
                            <button
                                onClick={handlePrevPage}
                                disabled={page === 1}
                                className={`flex items-center px-3 py-1 border rounded mr-2 text-base font-medium transition-colors duration-200 ${page === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                <span className="mr-1">&#8592;</span> Prev
                            </button>
                            <div className="flex space-x-2 justify-center flex-1">
                                {(() => {
                                    const pages = [];
                                    if (totalPages <= 5) {
                                        for (let p = 1; p <= totalPages; p++) {
                                            pages.push(p);
                                        }
                                    } else {
                                        if (page <= 3) {
                                            pages.push(1, 2, 3, 4, '...', totalPages);
                                        } else if (page >= totalPages - 2) {
                                            pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                                        } else {
                                            pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                                        }
                                    }
                                    return pages.map((p, idx) =>
                                        p === '...'
                                            ? <span key={"ellipsis-" + idx} className="w-7 h-7 flex items-center justify-center text-base text-gray-400">...</span>
                                            : <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`w-7 h-7 rounded text-base font-medium transition-colors duration-200 ${p === page ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {p}
                                            </button>
                                    );
                                })()}
                            </div>
                            <button
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                                className={`flex items-center px-3 py-1 border rounded ml-2 text-base font-medium transition-colors duration-200 ${page === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                Next <span className="ml-1">&#8594;</span>
                            </button>
                        </div>
                    )}
                </>
            )}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />
        </div>
    );
};

export default ManagementScratchOrders;
